import { useEffect, useState } from 'react';
import Page from '../../components/Page';
import { getAllVideos } from '../../services/videos';
import { Video } from '../../types/Video';
import VideoPage from '../../components/Videos/VideoPage';
import { useLocation, useNavigate } from 'react-router-dom';
import videoDarkThumbnail from '../../assets/images/video-dark-thumbnail.png';
import videoLightThumbnail from '../../assets/images/video-light-thumbnail.png';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
// import { useSystemTheme } from '../../hooks/useSystemTheme';
import { hapticsImpactLight } from '../../utils/haptics';
import { Skeleton, TextField, InputAdornment } from '@mui/material';
import { Play, Search } from 'react-feather';

interface GroupedVideos {
  [key: string]: Video[];
}

const Videos: React.FC = () => {
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);
  const [videos, setVideos] = useState<GroupedVideos>({});
  const [upNextVideos, setUpNextVideos] = useState<Video[]>([]);
  const [videosViewedInSequence, setVideosViewedInSequence] = useState<Video[]>(
    []
  );
  const [searchQuery, setSearchQuery] = useState('');
  const currentUser = useSelector((state: RootState) => state.user.profile);
  // const systemTheme = useSystemTheme();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getAllVideos().then((data: GroupedVideos) => {
      setVideos(data);
    });
  }, []);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length === 1) {
      setActiveVideo(null);
      const contentWindow = document.querySelector('.page_content');
      if (contentWindow) {
        contentWindow.scrollTo(0, 0);
      }
      window.scrollTo(0, 0);
    } else if (pathParts.length === 2) {
      const activeVideoId = pathParts[1];
      const activeVideo = Object.values(videos)
        .flat()
        .find((video) => video.id === activeVideoId);
      if (activeVideo) {
        setActiveVideo(activeVideo);
      }
    }
  }, [location, videos]);

  useEffect(() => {
    if (activeVideo) {
      // find array that contains activeVideo from videos
      const nextVideos = Object.values(videos).find((array) =>
        array.includes(activeVideo)
      );
      if (nextVideos) {
        const filteredNextVideos = nextVideos.filter(
          (video) => video.id !== activeVideo.id
        );
        const filteredNextVideosFromSequence = filteredNextVideos.filter(
          (video) => !videosViewedInSequence.includes(video)
        );
        const firstNineVideos = filteredNextVideosFromSequence.slice(0, 9);
        setUpNextVideos(firstNineVideos);
      }
    } else {
      setUpNextVideos([]);
      setVideosViewedInSequence([]);
    }
  }, [activeVideo, videos]);

  // Sort the topics alphabetically
  const sortedTopics = Object.keys(videos).sort();

  // Filter videos based on search query
  const filteredVideos = Object.entries(videos).reduce(
    (acc, [topic, videoList]) => {
      const filteredList = videoList.filter((video) =>
        video.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredList.length > 0) {
        acc[topic] = filteredList;
      }
      return acc;
    },
    {} as GroupedVideos
  );

  // Determine which thumbnail to use based on theme
  const getThumbnail = () => {
    if (
      currentUser?.theme === 'dark'
      // || (currentUser?.theme === 'system' && systemTheme === 'dark')
    ) {
      return videoDarkThumbnail;
    }
    return videoLightThumbnail;
  };

  const renderLoader = () => {
    return (
      <div className="videos-container">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="video-topic-section" key={index}>
            <Skeleton
              sx={{
                bgcolor: 'rgba(255,255,255,.1)',
                borderRadius: '.5rem',
                maxWidth: '100%',
              }}
              variant="rectangular"
              animation="wave"
              width={400}
              height={32}
            />
            <div className="video-grid m-t-1 m-b-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  sx={{
                    bgcolor: 'rgba(255,255,255,.1)',
                    borderRadius: '.5rem',
                    flex: '0 0 auto',
                    minWidth: '300px',
                    maxWidth: '100%',
                  }}
                  variant="rectangular"
                  animation="wave"
                  width={400}
                  height={200}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Page>
      <div className="videos">
        {!activeVideo && (
          <>
            <h1 className="m-t-0">Videos</h1>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              style={{ maxWidth: '780px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      size={20}
                      style={{ color: 'var(--type-body-inverse)' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {Object.keys(filteredVideos).length > 0 ? (
              <div className="videos-container">
                {sortedTopics.map(
                  (topic) =>
                    filteredVideos[topic] && (
                      <div key={topic} className="video-topic-section">
                        <h2 className="video-topic-title">{topic}</h2>
                        <div className="video-grid">
                          {filteredVideos[topic].map((video) => (
                            <button
                              key={video.id}
                              className="video-card"
                              style={
                                {
                                  '--video-thumbnail': `url(${getThumbnail()})`,
                                } as React.CSSProperties
                              }
                              onClick={() => {
                                setActiveVideo(video);
                                navigate(`/videos/${video.id}`);
                                setVideosViewedInSequence([video]);
                                hapticsImpactLight();
                                window.scrollTo(0, 0);
                              }}
                            >
                              <div className="play-button">
                                <Play />
                              </div>
                              <div className="video-info">
                                {video.title && <h3>{video.title}</h3>}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                )}
              </div>
            ) : (
              renderLoader()
            )}
          </>
        )}
        {activeVideo && (
          <VideoPage
            video={activeVideo}
            setActiveVideo={setActiveVideo}
            upNextVideos={upNextVideos}
            getThumbnail={getThumbnail}
            videosViewedInSequence={videosViewedInSequence}
            setVideosViewedInSequence={setVideosViewedInSequence}
          />
        )}
      </div>
    </Page>
  );
};

export default Videos;
